import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Clock,
  Hourglass,
  ListChecks,
  MoreVertical,
  Search,
  ShieldCheck,
  ShieldX,
  Timer,
  User,
  XCircle,
} from 'lucide-react';
import moment from 'moment';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import StatsGrid, { type Stat } from '@/client/components/common/StatsGrid';
import Pagination from '@/client/components/common/Pagination';
import { FilterChips, type FilterChip } from '@/client/pages/Admin/UsersAndPermissions/UserList/FilterChips';
import VerificationReviewDrawer from '@/client/components/admin/Verifications/VerificationReviewDrawer';
import {
  STATUS_BADGE,
  canClaim,
  canDecide,
  canExpire,
  canRevoke,
} from '@/client/components/admin/Verifications/verificationStatus';
import {
  useVerificationsQuery,
  useVerificationStatsQuery,
  useVerificationTypesQuery,
} from '@/client/api/verifications/verifications.query';
import { useVerificationActionMutation } from '@/client/api/verifications/verifications.mutation';
import type {
  Verification,
  VerificationLifecycleAction,
  VerificationScope,
  VerificationStatus,
  VerificationStatusFilter,
} from '@/client/types/verifications';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

const PAGE_SIZE = 25;

const TABS: { id: VerificationStatusFilter; label: string }[] = [
  { id: 'submitted', label: 'Submitted' },
  { id: 'in_review', label: 'In review' },
  { id: 'passed', label: 'Passed' },
  { id: 'failed', label: 'Failed' },
  { id: 'revoked', label: 'Revoked' },
  { id: 'all', label: 'All' },
];

const SCOPES: { id: VerificationScope | ''; label: string }[] = [
  { id: '', label: 'All subjects' },
  { id: 'user', label: 'Users' },
  { id: 'org', label: 'Organizations' },
  { id: 'listing', label: 'Listings' },
];

const SUBJECT_ICON = { user: User, organization: Building2, listing: ListChecks };

const SERIES = [
  { key: 'submitted', colour: '#2563eb' },
  { key: 'passed', colour: '#16a34a' },
  { key: 'failed', colour: '#dc2626' },
];

// Each entry is one row in the kebab. `confirm` copy is what the dialog asks,
// and anything with `reason` cannot be confirmed until one is typed.
const ACTIONS: {
  id: VerificationLifecycleAction;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  when: (s: VerificationStatus) => boolean;
  danger?: boolean;
  reason?: boolean;
  confirm: string;
}[] = [
  { id: 'claim', label: 'Take for review', icon: Hourglass, when: canClaim,
    confirm: 'Move this into review so other admins know you have it?' },
  { id: 'approve', label: 'Approve', icon: CheckCircle2, when: canDecide,
    confirm: 'Approve this verification? The subject becomes verified.' },
  { id: 'reject', label: 'Reject', icon: XCircle, when: canDecide,
    confirm: 'Reject this verification? The subject is told it did not pass.' },
  { id: 'revoke', label: 'Revoke', icon: ShieldX, when: canRevoke, danger: true, reason: true,
    confirm: 'Revoke this verification? It clears the verified flags. The record and its checklist are kept for audit.' },
  { id: 'expire', label: 'Expire', icon: Timer, when: canExpire,
    confirm: 'Expire this verification? It ages the record out and is not a judgement on the subject.' },
];

const VerificationsPage = () => {
  useBreadcrumbs([{ label: 'Verifications' }]);

  const { filters, page, searchTerm, debouncedSearchTerm, handleSearch, handleFilterChange, handlePageChange } =
    useListFilters<{ status: VerificationStatusFilter; scope: VerificationScope | ''; type: string }>({
      initialFilters: { status: 'submitted', scope: '', type: '' },
      initialLimit: PAGE_SIZE,
    });

  const params = useMemo(
    () => ({
      status: filters.status,
      scope: filters.scope || undefined,
      type: filters.type || undefined,
      q: debouncedSearchTerm || undefined,
      page,
      page_size: PAGE_SIZE,
    }),
    [filters.status, filters.scope, filters.type, debouncedSearchTerm, page],
  );

  const { data, isLoading, isError } = useVerificationsQuery(params);
  const { data: stats, isLoading: statsLoading } = useVerificationStatsQuery(30);
  const { data: types } = useVerificationTypesQuery();
  const action = useVerificationActionMutation();

  const [selected, setSelected] = useState<Verification | null>(null);
  const [pending, setPending] = useState<{ row: Verification; action: (typeof ACTIONS)[number] } | null>(null);
  const [reason, setReason] = useState('');

  const rows = data?.verifications ?? [];
  const counts = data?.counts;

  const waiting = stats?.oldest_waiting_hours ?? null;
  const tiles: Stat[] = [
    { key: 'submitted', label: 'Awaiting review', value: counts?.submitted ?? 0, icon: Clock,
      hint: 'Submitted and untouched' },
    { key: 'in_review', label: 'In review', value: counts?.in_review ?? 0, icon: Hourglass,
      hint: 'Claimed by an admin' },
    { key: 'passed', label: 'Passed', value: counts?.passed ?? 0, icon: CheckCircle2 },
    { key: 'failed', label: 'Failed', value: counts?.failed ?? 0, icon: XCircle },
    {
      key: 'oldest',
      label: 'Longest wait',
      // A real signal or nothing: a grey "unknown" tile would be noise.
      value: waiting == null ? 'Nothing waiting' : waiting >= 48 ? `${Math.round(waiting / 24)}d` : `${waiting}h`,
      icon: AlertTriangle,
      tone: waiting != null && waiting >= 48 ? 'text-red-600' : undefined,
      hint: waiting == null ? 'Queue is clear' : 'Oldest unreviewed submission',
    },
  ];

  const chips: FilterChip[] = [
    ...(filters.scope
      ? [{ key: 'scope', label: SCOPES.find((s) => s.id === filters.scope)?.label ?? filters.scope,
           onRemove: () => handleFilterChange('scope', '') }]
      : []),
    ...(filters.type ? [{ key: 'type', label: filters.type, onRemove: () => handleFilterChange('type', '') }] : []),
  ];

  const runAction = () => {
    if (!pending) return;
    action.mutate(
      { vid: pending.row.vid, action: pending.action.id, reason },
      { onSuccess: () => { setPending(null); setReason(''); setSelected(null); } },
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <ShieldCheck className="h-6 w-6 text-blue-700" />
            Verifications
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Identity, address and document checks across users, organizations and listings.
          </p>
        </div>

        <StatsGrid
          stats={tiles}
          isLoading={isLoading || statsLoading}
          className="lg:grid-cols-5 xl:grid-cols-5"
          activeKeys={[filters.status]}
          onStatClick={(key) => key !== 'oldest' && handleFilterChange('status', key as VerificationStatusFilter)}
        />

        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Submissions over 30 days</h2>
            <div className="flex gap-3 text-xs text-gray-500">
              {SERIES.map((s) => (
                <span key={s.key} className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.colour }} />
                  {s.key}
                </span>
              ))}
            </div>
          </div>
          {statsLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (stats?.series ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-500">No verifications in the last 30 days.</p>
          ) : (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.series ?? []} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false}
                         tickFormatter={(d: string) => moment(d).format('D MMM')} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip labelFormatter={(d) => moment(String(d)).format('ll')} />
                  {SERIES.map((s) => (
                    <Area key={s.key} type="monotone" dataKey={s.key} stackId="1" stroke={s.colour}
                          fill={s.colour} fillOpacity={0.15} />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 rounded-lg bg-white p-1 shadow-sm">
            {TABS.map((tab) => {
              const active = filters.status === tab.id;
              const count = tab.id !== 'all' ? counts?.[tab.id as VerificationStatus] : undefined;
              return (
                <button key={tab.id} type="button" onClick={() => handleFilterChange('status', tab.id)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                  {tab.label}
                  {count !== undefined && count > 0 && (
                    <span className={`ml-1.5 text-xs ${active ? 'text-blue-100' : 'text-gray-400'}`}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select value={filters.scope} onChange={(e) => handleFilterChange('scope', e.target.value)}
              className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700">
              {SCOPES.map((s) => <option key={s.id || 'any'} value={s.id}>{s.label}</option>)}
            </select>
            <select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}
              className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-gray-700">
              <option value="">All types</option>
              {(types ?? []).map((t) => <option key={t.vtypid} value={t.name_system}>{t.name_system}</option>)}
            </select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input value={searchTerm} onChange={(e) => handleSearch(e.target.value)}
                placeholder="Name or verification id" className="h-9 w-56 pl-8" />
            </div>
          </div>
        </div>

        <FilterChips chips={chips} />

        <div className="overflow-hidden rounded-lg bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                {['Subject', 'Type', 'Status', 'Updated', ''].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}><td className="px-4 py-3" colSpan={5}><Skeleton className="h-5 w-full" /></td></tr>
                ))}

              {!isLoading && isError && (
                <tr><td className="px-4 py-10 text-center text-gray-500" colSpan={5}>Could not load verifications.</td></tr>
              )}

              {!isLoading && !isError && rows.length === 0 && (
                <tr><td className="px-4 py-10 text-center text-gray-500" colSpan={5}>
                  {filters.status === 'submitted' ? 'Nothing is waiting for review.' : 'No verifications match these filters.'}
                </td></tr>
              )}

              {rows.map((v) => {
                const Icon = v.subject.kind ? SUBJECT_ICON[v.subject.kind] : ShieldCheck;
                const available = ACTIONS.filter((a) => a.when(v.status));
                return (
                  <tr key={v.vid} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelected(v)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 shrink-0 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">{v.subject.label ?? `vid ${v.vid}`}</div>
                          <div className="text-xs text-gray-500">
                            {v.subject.kind ?? 'no subject'}{v.subject.id ? ` · ${v.subject.id}` : ''} · vid {v.vid}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{v.type_name ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[v.status]}`}>
                        {v.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {v.date_modified ? moment(v.date_modified).fromNow() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" aria-label={`Actions for verification ${v.vid}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => setSelected(v)}>
                            <ListChecks className="mr-2 h-4 w-4" /> Open checklist
                          </DropdownMenuItem>
                          {available.length > 0 && <DropdownMenuSeparator />}
                          {available.map((a) => (
                            <DropdownMenuItem key={a.id} className={a.danger ? 'text-red-600' : undefined}
                              onClick={() => { setReason(''); setPending({ row: v, action: a }); }}>
                              <a.icon className="mr-2 h-4 w-4" /> {a.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {data && data.total > PAGE_SIZE && (
          <Pagination total={data.total} page={page} limit={PAGE_SIZE} setPage={handlePageChange} />
        )}
      </div>

      <VerificationReviewDrawer
        verification={selected}
        onClose={() => setSelected(null)}
        onAction={(row, id) => {
          const a = ACTIONS.find((x) => x.id === id);
          if (a) { setReason(''); setPending({ row, action: a }); }
        }}
      />

      <AlertDialog open={Boolean(pending)} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.action.label} verification</AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.action.confirm}
              {pending && (
                <span className="mt-2 block text-gray-500">
                  {pending.row.subject.label ?? `vid ${pending.row.vid}`} · {pending.row.type_name ?? 'unknown type'}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pending?.action.reason && (
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus
              placeholder="Reason (recorded against the verification)"
              className="w-full rounded-md border border-gray-200 p-2 text-sm" />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={action.isPending || (pending?.action.reason ? !reason.trim() : false)}
              className={pending?.action.danger ? 'bg-red-600 hover:bg-red-700' : undefined}
              onClick={(e) => { e.preventDefault(); runAction(); }}>
              {action.isPending ? 'Working…' : pending?.action.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VerificationsPage;
